import {
  useEffect,
  useState,
  ComponentProps,
  ForwardRefExoticComponent,
  forwardRef,
  ComponentPropsWithoutRef,
  createContext,
  useContext,
} from 'react';
import { withStyles } from 'vopay';
import styles from './TableFiltersContainer.module.scss';
import * as Collapsible from '@radix-ui/react-collapsible';
import { UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type RootProps = ComponentPropsWithoutRef<typeof Collapsible.Root> & {
  filtersApplied: boolean;
};

const Context = createContext({ filtersApplied: false, open: false });

/**
 * @param {boolean} open - Whether or not the filters are currently open.
 * @param {function} onOpenChange - Callback for when the filters are opened or closed.
 * @param {boolean} filtersApplied - Whether or not filters are currently applied.
 *
 * @example
 * Basic
 * ```
 * <TableFiltersContainer open={open} onOpenChange={setOpen} filtersApplied={filtersApplied}>
 *   <TableFiltersContainer.Trigger />
 *
 *   <TableFiltersContainer.Content>
 *     <form className={cn('filters')} onSubmit={handleSubmit}>
 *       {renderFormElements()}
 *
 *       <Button type="submit" colorScheme="primary">
 *         Search
 *       </Button>
 *     </form>
 *   </TableFiltersContainer.Content>
 * </TableFiltersContainer>
 * ```
 *
 * @example
 * Put some other stuff on the same line as the trigger:
 * ```
 * <TableFiltersContainer open={open} onOpenChange={setOpen} filtersApplied={filtersApplied}>
 *   <div
 *     style={{
 *       display: 'flex',
 *       justifyContent: 'space-between',
 *       alignItems: 'center',
 *       gap: '8px',
 *     }}
 *   >
 *     <div>hello</div>
 *     <TableFiltersContainer.Trigger />
 *   </div>
 *
 *   ...
 * </TableFiltersContainer>
 * ```
 */
const Root = ({ filtersApplied, ...props }: RootProps) => {
  const [open, setOpen] = useState(props.open ?? true);

  useEffect(() => {
    if (filtersApplied) setOpen(true);
  }, [filtersApplied]);

  return (
    <Context.Provider value={{ filtersApplied, open }}>
      <Collapsible.Root
        {...props}
        open={open}
        onOpenChange={(e) => {
          setOpen(e);
          props.onOpenChange?.(e);
        }}
        className={cn('tableFiltersContainerRoot', props.className)}
      />
    </Context.Provider>
  );
};

type TriggerComponent = ForwardRefExoticComponent<ComponentProps<typeof Collapsible.Trigger>>;

const Trigger: TriggerComponent = forwardRef((props, ref) => {
  const { filtersApplied, open } = useContext(Context);
  const { t } = useTranslation('translations', { keyPrefix: 'UI.TableFilters' });

  return (
    <Collapsible.Trigger ref={ref} asChild {...props} className={cn('tableFiltersContainerTrigger', props.className)}>
      <UtilButton
        iconLeftProps={{
          icon: 'filter_alt',
          className: cn('filterIcon', filtersApplied && 'changed'),
        }}
      >
        {open ? t('triggerButtonHide') : t('triggerButtonShow')}
      </UtilButton>
    </Collapsible.Trigger>
  );
});

type ContentComponent = ForwardRefExoticComponent<ComponentProps<typeof Collapsible.Content>>;

const Content: ContentComponent = forwardRef((props, ref) => {
  return (
    <Collapsible.Content
      ref={ref}
      {...props}
      className={cn('tableFiltersContainerContent', props.className)}
      data-testid={`tableFiltersContent${props.id ? '-' + props.id : ''}`}
    />
  );
});

Root.Content = Content;
Root.Trigger = Trigger;

export default Root;
